/**
 * Client-owned redux state for the site scheduling calendar.
 *
 * Holds the event series definitions (keyed by id, the source the event dialog
 * reads recurrence descriptors from), the server-expanded occurrences for the
 * currently fetched date window, that window's bounds, and the view substate
 * (which calendar view is shown, the visible date, the selected relay/event).
 * Relays are read from the site dashboard snapshot, not stored here.
 *
 * Contains:
 * - the calendar reducer (default export) + view/loading/error actions
 * - selectors for the view substate, series, occurrences, and fetched window
 */
import { createSelector, createSlice } from '@reduxjs/toolkit';
import { format, parse } from 'date-fns';
import { produce } from 'immer';

import { DEFAULT_DATE_FORMAT } from '@sophocles/app/constants';
import {
  createEvent,
  deleteEvent,
  editEvent,
  fetchCalendarOccurrences,
  loadCalendarSeries,
} from '@sophocles/features/calendar/api';

export const RECURRENCE_CHOICES = ['never', 'daily', 'weekly', 'monthly'];

const keyById = events => Object.fromEntries((events ?? []).map(event => [event.id, event]));

const upsertSeriesRow = (state, event) => {
  if (event && event.id != null) {
    state.seriesById[event.id] = event;
  }
};

const slice = createSlice({
  name: 'calendar',
  initialState: {
    view: {
      date: format(new Date(), DEFAULT_DATE_FORMAT),
      selectedRelayId: null,
      selectedEventId: null,
      mode: 'month',
    },
    seriesById: {},
    occurrences: [],
    fetchedWindow: { start: null, end: null, fetchedAt: null },
    timezone: null,
    lastGeneratedAt: null,
    loading: false,
    error: null,
  },
  reducers: {
    upsertSeries(state, action) {
      const { events = [], generatedAt = null } = action.payload;
      events.forEach(event => upsertSeriesRow(state, event));
      if (generatedAt != null) {
        state.lastGeneratedAt = generatedAt;
      }
    },
    setDate(state, action) {
      const { payload } = action;
      state.view.date = payload instanceof Date ? format(payload, DEFAULT_DATE_FORMAT) : payload;
    },
    setSelectedRelayId(state, action) {
      state.view.selectedRelayId = action.payload;
    },
    setSelectedEventId(state, action) {
      state.view.selectedEventId = action.payload;
    },
    setMode(state, action) {
      state.view.mode = action.payload;
    },
    setLoading(state, action) {
      state.loading = action.payload;
    },
    setError(state, action) {
      state.error = action.payload;
    },
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(loadCalendarSeries.fulfilled, (state, action) => {
        state.seriesById = keyById(action.payload);
      })

      .addCase(fetchCalendarOccurrences.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCalendarOccurrences.fulfilled, (state, action) => {
        state.loading = false;
        const { occurrences = [], timezone = null } = action.payload ?? {};
        state.occurrences = occurrences;
        state.timezone = timezone;
        // Record the window we REQUESTED (client `yyyy-MM-dd`), not the server's
        // echoed bounds, so the coverage check compares like-for-like — the
        // server may clamp or reformat them, which would read as never-covered
        // and refetch forever.
        const { start = null, end = null } = action.meta.arg ?? {};
        state.fetchedWindow = { start, end, fetchedAt: Date.now() };
      })
      .addCase(fetchCalendarOccurrences.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(createEvent.fulfilled, (state, action) => {
        upsertSeriesRow(state, action.payload);
      })
      .addCase(editEvent.fulfilled, (state, action) => {
        const { payload } = action;
        if (payload && payload.parent) {
          upsertSeriesRow(state, payload.parent);
          upsertSeriesRow(state, payload.override);
        } else {
          upsertSeriesRow(state, payload);
        }
      })
      .addCase(deleteEvent.fulfilled, (state, action) => {
        const { payload } = action;
        if (payload && typeof payload === 'object') {
          upsertSeriesRow(state, payload.parent);
        } else if (payload != null) {
          delete state.seriesById[payload];
        }
      });
  },
});

export const selectCalendar = state => state.calendar;

export const selectCalendarViewProperties = createSelector(selectCalendar, state =>
  produce(state, draft => {
    draft.view.date = parse(draft.view.date, DEFAULT_DATE_FORMAT, new Date());
    return draft;
  }),
);

export const selectSelectedRelayId = createSelector(selectCalendar, state => state.view.selectedRelayId);
export const selectSelectedEventId = createSelector(selectCalendar, state => state.view.selectedEventId);

export const selectSeriesById = state => state.calendar.seriesById;
export const selectAllSeries = createSelector(selectSeriesById, byId => Object.values(byId));
export const selectEvent = id => state => state.calendar.seriesById[id] ?? null;

export const selectCalendarOccurrences = state => state.calendar.occurrences;
export const selectCalendarTimezone = state => state.calendar.timezone;
export const selectFetchedWindow = state => state.calendar.fetchedWindow;
export const selectCalendarLoading = state => state.calendar.loading;

export const {
  upsertSeries,
  setMode: setCalendarMode,
  setDate: setCalendarDate,
  setSelectedRelayId: setCalendarSelectedRelayId,
  setSelectedEventId,
  setLoading,
  setError,
  clearError,
} = slice.actions;

const { reducer } = slice;
export default reducer;
